import {
  Button,
  Checkbox,
  Modal,
  Text,
  useMediaQuery,
} from '@querodelivery/uni-react';
import { Form } from '@radix-ui/react-form';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { CalendarInput } from '@/_components/calendar-input/calendar-input';
import styles from './create-mission-modal.module.scss';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import { useCities } from '@/_hooks/use-cities';
import {
  convertMoneyInputFormatToFloat,
  removeNoNumeric,
} from '@/Utils/FnUtils';
import { useAppSelector } from '@/_store/hooks';
import { selectUser } from '@/_store/modules/auth/selectors';
import { usePostMission } from '../../api/post-missions';
import { usePutMission } from '../../api/put-mission';
import type { CreateMissionFormType } from '../../types';
import {
  combineDateAndTime,
  CREATE_MISSION_FORM_DEFAULT_VALUES,
  CREATE_MISSION_FORM_SCHEMA,
  EMissionType,
  getMissionMinStartDate,
  MISSION_DESCRIPTION_DEFAULT,
  MISSION_NAME_MAX_LENGTH,
  MISSION_OBJECTIVE_REWARD_FIELDS,
  MISSION_PERIOD_FIELDS,
  MISSION_TITLE_DEFAULT,
  TARGET_AUDIENCE,
  type TargetAudience,
} from '../../constants';
import {
  mapCreateMissionFormToPayload,
  mapCreateMissionFormToUpdatePayload,
} from '../../utils/map-create-mission-form-to-payload';
import { ConfirmMissionModal } from '../confirm-mission-modal/confirm-mission-modal';
import { MissionMobilePreview } from '../mission-mobile-preview/mission-mobile-preview';

interface CreateMissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  missionId?: string;
  defaultValues?: CreateMissionFormType | null;
}

const buildPeriodValue = (dateValue?: Date, timeValue?: Date) => {
  if (!dateValue) return timeValue ?? null;
  if (!timeValue) return dateValue;

  return combineDateAndTime(dateValue, timeValue);
};

export const CreateMissionModal = ({
  isOpen,
  onClose,
  missionId,
  defaultValues,
}: CreateMissionModalProps) => {
  const { CITIES_OPTIONS, loadingCities } = useCities();
  const user = useAppSelector(selectUser);
  const isMobile = useMediaQuery('lg-down');
  const showPreview = !isMobile;
  const isEdit = !!missionId;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const isConfirmOpenRef = useRef(false);
  const isReturningToCreateRef = useRef(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    trigger,
    reset,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm<CreateMissionFormType, { isEdit?: boolean }>({
    resolver: yupResolver(CREATE_MISSION_FORM_SCHEMA),
    context: { isEdit },
    defaultValues: CREATE_MISSION_FORM_DEFAULT_VALUES,
    shouldFocusError: false,
  });

  const { mutate: createMission, isLoading: isCreating } = usePostMission();
  const { mutate: updateMission, isLoading: isUpdating } = usePutMission();
  const isLoading = isCreating || isUpdating;
  const areLockedFieldsDisabled = isLoading || isEdit;
  const modalTitle = isEdit ? 'Editar missão' : 'Cadastrar missão';

  const targetAudience = watch('targetAudience');
  const deliverymanCsv = watch('deliverymanCsv');
  const [startDate, startTime, endDate, endTime] = watch(MISSION_PERIOD_FIELDS);
  const [objective01, objective02, objective03, reward01, reward02, reward03] =
    watch(MISSION_OBJECTIVE_REWARD_FIELDS);
  const formValues = watch();
  const minStartDate = getMissionMinStartDate();

  const isCreateOpen = isOpen && !isConfirmOpen;

  // O Uni Modal chama onClose ao terminar a animação de saída. Ao trocar para
  // a confirmação o modal de criação fecha e esse callback dispararia o onClose
  // do pai, derrubando o fluxo inteiro — por isso ignoramos nesse caso.
  const handleCreateModalClose = () => {
    if (isConfirmOpenRef.current) return;
    onClose();
  };

  const dismissFlow = () => {
    if (isLoading) return;
    isReturningToCreateRef.current = false;
    isConfirmOpenRef.current = false;
    setIsConfirmOpen(false);
    onClose();
  };

  const handleConfirmModalClose = () => {
    if (isReturningToCreateRef.current) {
      isReturningToCreateRef.current = false;
      return;
    }
    dismissFlow();
  };

  useEffect(() => {
    if (!isSubmitted) return;

    trigger(MISSION_PERIOD_FIELDS);
  }, [startDate, startTime, endDate, endTime, isSubmitted]);

  useEffect(() => {
    if (!isSubmitted) return;

    trigger(MISSION_OBJECTIVE_REWARD_FIELDS);
  }, [
    objective01,
    objective02,
    objective03,
    reward01,
    reward02,
    reward03,
    isSubmitted,
  ]);

  useEffect(() => {
    if (!isOpen) {
      isConfirmOpenRef.current = false;
      isReturningToCreateRef.current = false;
      setIsConfirmOpen(false);
      reset(CREATE_MISSION_FORM_DEFAULT_VALUES);
      return;
    }

    reset(defaultValues ?? CREATE_MISSION_FORM_DEFAULT_VALUES);
  }, [isOpen, defaultValues, reset]);

  const handleTargetAudienceChange = (audience: TargetAudience) => {
    if (isEdit) return;

    setValue('targetAudience', audience, { shouldValidate: true });
    setValue('cityIds', []);
    setValue('deliverymanIds', []);
    setValue('deliverymanCsv', null);
    clearErrors(['cityIds', 'deliverymanIds', 'deliverymanCsv']);
  };

  const onSubmit: SubmitHandler<CreateMissionFormType> = () => {
    isReturningToCreateRef.current = false;
    isConfirmOpenRef.current = true;
    setIsConfirmOpen(true);
  };

  const handleCancelConfirm = () => {
    isReturningToCreateRef.current = true;
    isConfirmOpenRef.current = false;
    setIsConfirmOpen(false);
  };

  const closeAfterSubmit = () => {
    reset(CREATE_MISSION_FORM_DEFAULT_VALUES);
    isReturningToCreateRef.current = false;
    isConfirmOpenRef.current = false;
    setIsConfirmOpen(false);
    onClose();
  };

  const handleConfirmMission = () => {
    if (!user?._id) {
      toast.error('Não foi possível identificar o usuário logado.');
      return;
    }

    const payload = mapCreateMissionFormToPayload(getValues(), user._id);
    createMission(payload, {
      onSuccess: closeAfterSubmit,
    });
  };

  const handleUpdateMission = () => {
    if (!missionId) {
      toast.error('Não foi possível identificar a missão.');
      return;
    }

    const payload = mapCreateMissionFormToUpdatePayload(getValues(), missionId);
    updateMission(payload, {
      onSuccess: closeAfterSubmit,
    });
  };

  // TODO: Add depois
  const showTitleAndDescription = false;

  return (
    <>
      <Modal
        isOpen={isCreateOpen}
        onClose={handleCreateModalClose}
        header={showPreview ? undefined : modalTitle}
        showCloseIcon={!showPreview}
        width={showPreview ? 'auto' : '60%'}
        className={showPreview ? styles.modalShell : undefined}
        contentClassName={showPreview ? styles.modalShellContent : undefined}
        variant={isMobile ? 'bottomFullPage' : 'floating'}
      >
        <div className={showPreview ? styles.layout : undefined}>
          {showPreview && <MissionMobilePreview formValues={formValues} />}
          <div className={showPreview ? styles.formCard : undefined}>
            {showPreview && (
              <div className={styles.formHeader}>
                <Text
                  as="h2"
                  variant="headingXl"
                  fontWeight="bold"
                  tone="highlight"
                >
                  {modalTitle}
                </Text>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={dismissFlow}
                  aria-label="Fechar modal"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            )}
            <Form
              className={styles.form}
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div className={styles.line}>
                {isMobile ? (
                  <>
                    <Controller
                      control={control}
                      name="startDate"
                      render={({ field: { value, onChange } }) => (
                        <CalendarInput
                          required
                          label="Data/Hora inicial"
                          name="startDate"
                          value={buildPeriodValue(value, startTime)}
                          selectsStart
                          withPortal={isMobile}
                          onChange={selected => {
                            onChange(selected);
                            setValue('startTime', selected as Date);
                          }}
                          disabled={isLoading}
                          errorMessage={
                            errors.startDate?.message ??
                            errors.startTime?.message
                          }
                          minDate={minStartDate}
                          timeInputLabel="Horário:"
                          clearable
                          showTimeInput
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="endDate"
                      render={({ field: { value, onChange } }) => (
                        <CalendarInput
                          required
                          label="Data/Hora final"
                          name="endDate"
                          value={buildPeriodValue(value, endTime)}
                          onChange={selected => {
                            onChange(selected);
                            setValue('endTime', selected as Date);
                          }}
                          disabled={isLoading}
                          errorMessage={
                            errors.endDate?.message ?? errors.endTime?.message
                          }
                          withPortal={isMobile}
                          todayButton="Hoje"
                          minDate={startDate}
                          timeInputLabel="Horário:"
                          showTimeInput
                          clearable
                          popperPlacement="top"
                          selectsEnd
                        />
                      )}
                    />
                  </>
                ) : (
                  <>
                    <Controller
                      control={control}
                      name="startDate"
                      render={({ field: { value, onChange } }) => (
                        <CalendarInput
                          required
                          label="Data inicial"
                          name="startDate"
                          value={value}
                          onChange={onChange}
                          disabled={isLoading}
                          minDate={minStartDate}
                          errorMessage={errors.startDate?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="startTime"
                      render={({ field: { value, onChange } }) => (
                        <CalendarInput
                          required
                          label="Hora inicial"
                          name="startTime"
                          mode="time"
                          value={value}
                          onChange={onChange}
                          disabled={isLoading}
                          errorMessage={errors.startTime?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="endDate"
                      render={({ field: { value, onChange } }) => (
                        <CalendarInput
                          required
                          label="Data final"
                          name="endDate"
                          value={value}
                          onChange={onChange}
                          disabled={isLoading}
                          minDate={startDate || undefined}
                          errorMessage={errors.endDate?.message}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="endTime"
                      render={({ field: { value, onChange } }) => (
                        <CalendarInput
                          required
                          label="Hora final"
                          name="endTime"
                          mode="time"
                          value={value}
                          onChange={onChange}
                          disabled={isLoading}
                          errorMessage={errors.endTime?.message}
                        />
                      )}
                    />
                  </>
                )}
              </div>

              <div className={styles.targetAudience}>
                <Text
                  variant="bodyMd"
                  tone="highlight"
                  fontWeight="semibold"
                  className={styles.targetAudienceLabel}
                >
                  Para quem você está criando esta missão?
                  <span className={styles.requiredIndicator} aria-hidden="true">
                    {' '}
                    *
                  </span>
                </Text>
                <div className={styles.checkboxes}>
                  <div className={styles.checkboxGroup}>
                    <Checkbox
                      labelPosition="right"
                      label="Para municípios"
                      name="city"
                      checked={targetAudience === TARGET_AUDIENCE.CITY}
                      disabled={areLockedFieldsDisabled}
                      onChange={() =>
                        handleTargetAudienceChange(TARGET_AUDIENCE.CITY)
                      }
                    />
                    <Checkbox
                      labelPosition="right"
                      label="Para entregadores"
                      name="deliveryman"
                      checked={targetAudience === TARGET_AUDIENCE.DELIVERYMAN}
                      disabled={areLockedFieldsDisabled}
                      onChange={() =>
                        handleTargetAudienceChange(TARGET_AUDIENCE.DELIVERYMAN)
                      }
                    />
                  </div>
                  {errors.targetAudience?.message && (
                    <span className={styles.errorMessage}>
                      {errors.targetAudience.message}
                    </span>
                  )}
                </div>
              </div>

              {targetAudience === EMissionType.CITY && (
                <Controller
                  control={control}
                  name="cityIds"
                  render={({ field: { value, onChange } }) => (
                    <DropdownInput
                      multiple
                      search
                      selectionDisplay="tags"
                      required
                      label="Municípios:"
                      placeholder="Selecione os municípios"
                      options={CITIES_OPTIONS}
                      loading={loadingCities || isLoading}
                      disabled={areLockedFieldsDisabled}
                      value={value}
                      onChange={selected => onChange(selected as string[])}
                      errorMessage={
                        typeof errors.cityIds?.message === 'string'
                          ? errors.cityIds.message
                          : undefined
                      }
                    />
                  )}
                />
              )}

              {targetAudience === EMissionType.DELIVERYMAN && (
                <Controller
                  control={control}
                  name="deliverymanIds"
                  render={({ field: { value, onChange } }) => (
                    <DropdownInput
                      multiple
                      allowCustomValues
                      allowCsvImport={!isEdit}
                      required
                      label="Entregadores:"
                      placeholder="ID dos entregadores."
                      value={value}
                      disabled={areLockedFieldsDisabled}
                      onChange={selected => {
                        if (deliverymanCsv) {
                          setValue('deliverymanCsv', null);
                        }
                        onChange(selected as string[]);
                      }}
                      onCsvImport={file => {
                        setValue('deliverymanCsv', file, {
                          shouldValidate: true,
                        });
                        onChange([]);
                      }}
                      errorMessage={
                        typeof errors.deliverymanIds?.message === 'string'
                          ? errors.deliverymanIds.message
                          : undefined
                      }
                    />
                  )}
                />
              )}

              <Controller
                control={control}
                name="missionName"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    required
                    name="missionName"
                    label="Nome da missão:"
                    placeholder="nome da missão"
                    maxLength={MISSION_NAME_MAX_LENGTH}
                    value={value}
                    onChange={onChange}
                    disabled={isLoading}
                    errorMessage={errors.missionName?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="budget"
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    required
                    name="budget"
                    label="Orçamento da missão:"
                    placeholder="orçamento previsto da missão"
                    tag="R$"
                    value={value}
                    disabled={isLoading}
                    onChange={e => {
                      onChange(convertMoneyInputFormatToFloat(e.target.value));
                    }}
                    errorMessage={errors.budget?.message}
                  />
                )}
              />

              <div className={styles.objectivesAndRewards}>
                <div className={styles.line}>
                  <Controller
                    control={control}
                    name="objective01"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        required
                        name="objective01"
                        label="Objetivo 01:"
                        placeholder="quantidade de entregas"
                        inputMode="numeric"
                        value={value}
                        disabled={isLoading}
                        onChange={e =>
                          onChange(removeNoNumeric(e.target.value))
                        }
                        errorMessage={errors.objective01?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="objective02"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        name="objective02"
                        label="Objetivo 02:"
                        placeholder="quantidade de entregas"
                        inputMode="numeric"
                        value={value}
                        disabled={isLoading}
                        onChange={e =>
                          onChange(removeNoNumeric(e.target.value))
                        }
                        errorMessage={errors.objective02?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="objective03"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        name="objective03"
                        label="Objetivo 03:"
                        placeholder="quantidade de entregas"
                        inputMode="numeric"
                        value={value}
                        disabled={isLoading}
                        onChange={e =>
                          onChange(removeNoNumeric(e.target.value))
                        }
                        errorMessage={errors.objective03?.message}
                      />
                    )}
                  />
                </div>
                <div className={styles.line}>
                  <Controller
                    control={control}
                    name="reward01"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        required
                        name="reward01"
                        label="Premiação 01:"
                        placeholder="premiação da missão"
                        tag="R$"
                        value={value}
                        disabled={isLoading}
                        onChange={e => {
                          onChange(
                            convertMoneyInputFormatToFloat(e.target.value),
                          );
                        }}
                        errorMessage={errors.reward01?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="reward02"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        name="reward02"
                        label="Premiação 02:"
                        placeholder="premiação da missão"
                        tag="R$"
                        value={value}
                        disabled={isLoading}
                        onChange={e => {
                          onChange(
                            convertMoneyInputFormatToFloat(e.target.value),
                          );
                        }}
                        errorMessage={errors.reward02?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="reward03"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        name="reward03"
                        label="Premiação 03:"
                        placeholder="premiação da missão"
                        tag="R$"
                        value={value}
                        disabled={isLoading}
                        onChange={e => {
                          onChange(
                            convertMoneyInputFormatToFloat(e.target.value),
                          );
                        }}
                        errorMessage={errors.reward03?.message}
                      />
                    )}
                  />
                </div>
              </div>

              {showTitleAndDescription && (
                <div className={styles.line}>
                  <Controller
                    control={control}
                    name="missionTitle"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        name="missionTitle"
                        label="Título: "
                        placeholder={MISSION_TITLE_DEFAULT}
                        value={value}
                        disabled={isLoading}
                        onChange={onChange}
                        errorMessage={errors.missionTitle?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="missionDescription"
                    render={({ field: { value, onChange } }) => (
                      <TextInput
                        name="missionDescription"
                        label="Descrição da missão:"
                        placeholder={MISSION_DESCRIPTION_DEFAULT}
                        value={value}
                        disabled={isLoading}
                        onChange={onChange}
                        errorMessage={errors.missionDescription?.message}
                      />
                    )}
                  />
                </div>
              )}

              <div className={styles.actions}>
                <Button
                  variant="outline"
                  size="lg"
                  type="button"
                  onClick={dismissFlow}
                >
                  voltar
                </Button>
                <Button size="lg" type="submit" variant="cta">
                  {isEdit ? 'salvar alterações' : 'criar missão'}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </Modal>

      <ConfirmMissionModal
        isOpen={isOpen && isConfirmOpen}
        formValues={formValues}
        isLoading={isLoading}
        isEdit={isEdit}
        onClose={handleConfirmModalClose}
        onCancel={handleCancelConfirm}
        onConfirm={isEdit ? handleUpdateMission : handleConfirmMission}
      />
    </>
  );
};
